import { useTranslation } from "react-i18next";

import type { CockpitPublicationById } from "../api/useCockpitPublicationById";

import Icons from "@/components/icons";
import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { Card } from "@/components/ui/v2/card";
import { KeyValueCardContent } from "@/components/ui/v2/key-value-card-content";
import { formatDate } from "@/lib/helpers/formatDate";
import type { VacancyType } from "@/types/vacancy/VacancyType";

const PUBLICATION_STATUS_MAP: Record<string, "concept" | "online" | "paused" | "inactive"> = {
  concept: "concept",
  online: "online",
  gepauzeerd: "paused",
  inactief: "inactive",
};

const PUBLICATION_STATUS_VARIANT: Record<
  "concept" | "online" | "paused" | "inactive",
  "neutral" | "success" | "warning" | "secondary"
> = {
  concept: "secondary",
  online: "success",
  paused: "warning",
  inactive: "neutral",
};

function PublicationStatusBadge({ statusName }: { statusName?: string | null }) {
  const { t } = useTranslation();
  if (!statusName?.trim()) return null;
  const statusKey = PUBLICATION_STATUS_MAP[statusName.trim().toLowerCase()];
  if (!statusKey) return null;
  const variant = PUBLICATION_STATUS_VARIANT[statusKey];
  const label = t(`integrations.cockpit.jobSource.publications.status.${statusKey}`);
  return (
    <Badge variant={variant} size="medium" className="w-fit">
      {variant === "success" && <Icons.CheckCircleIcon fontSize={16} />}
      {variant === "warning" && <Icons.WarningIcon fontSize={16} />}
      {label}
    </Badge>
  );
}

interface PublicationComparisonProps {
  publication: CockpitPublicationById | null | undefined;
  vacancy: VacancyType | null | undefined;
  isLoadingPublication: boolean;
  isLoadingVacancy: boolean;
  onSync?: () => void;
  isSyncing?: boolean;
}

function valueNode(value: string | null | undefined) {
  return <span className="text-sm break-words">{value ?? "-"}</span>;
}

export function PublicationComparison({
  publication,
  vacancy,
  isLoadingPublication,
  isLoadingVacancy,
  onSync,
  isSyncing = false,
}: PublicationComparisonProps) {
  const { t } = useTranslation();

  const isPublicationOnline =
    publication?.status?.name?.trim().toLowerCase() === "online";
  const canSync = Boolean(onSync && isPublicationOnline);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
      <Card className="p-4 h-full flex flex-col min-h-0">
        {isLoadingPublication ? (
          <div className="animate-pulse space-y-2 flex-1">
            <div className="h-4 bg-muted rounded-lg w-3/4" />
            <div className="h-3 bg-muted rounded-lg w-full" />
            <div className="h-3 bg-muted rounded-lg w-5/6" />
          </div>
        ) : publication ? (
          <KeyValueCardContent
            rows={[
              { label: t("integrations.cockpit.jobSource.publications.comparison.labels.id"), value: valueNode(publication.id) },
              { label: t("common.title"), value: valueNode(publication.title) },
              { label: t("integrations.cockpit.jobSource.publications.comparison.labels.summary"), value: valueNode(publication.descriptions?.summary) },
              { label: t("integrations.cockpit.jobSource.publications.comparison.labels.publicationDate"), value: publication.publicationDate?.start ? valueNode(formatDate(new Date(publication.publicationDate.start))) : null },
              { label: t("integrations.cockpit.jobSource.publications.comparison.labels.expirationDate"), value: publication.publicationDate?.end ? valueNode(formatDate(new Date(publication.publicationDate.end))) : null },
            ]}
            badge={
              publication.status?.name ? (
                <PublicationStatusBadge statusName={publication.status.name} />
              ) : undefined
            }
            className="flex-1 min-h-0 space-y-3"
          />
        ) : (
          <p className="text-sm text-muted-foreground">{t("common.none")}</p>
        )}
      </Card>

      {isLoadingVacancy ? (
        <Card className="p-4 h-full flex flex-col min-h-0">
          <div className="animate-pulse space-y-2 flex-1">
            <div className="h-4 bg-muted rounded-lg w-3/4" />
            <div className="h-3 bg-muted rounded-lg w-full" />
            <div className="h-3 bg-muted rounded-lg w-5/6" />
          </div>
        </Card>
      ) : vacancy ? (
        <Card className="p-4 h-full flex flex-col min-h-0">
          <KeyValueCardContent
            rows={[
              { label: t("integrations.cockpit.jobSource.publications.comparison.labels.publicationId"), value: valueNode(vacancy.publID) },
              { label: t("common.title"), value: valueNode(vacancy.title) },
              { label: t("integrations.cockpit.jobSource.publications.comparison.labels.summary"), value: valueNode(vacancy.summary) },
              { label: t("integrations.cockpit.jobSource.publications.comparison.labels.publicationDate"), value: vacancy.actual ? valueNode(formatDate(new Date(vacancy.actual))) : null },
              { label: t("integrations.cockpit.jobSource.publications.comparison.labels.expirationDate"), value: vacancy.expires ? valueNode(formatDate(new Date(vacancy.expires))) : null },
            ]}
            badge={
              onSync ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onSync}
                  disabled={isSyncing}
                >
                  <Icons.SyncIcon
                    fontSize={14}
                    className={isSyncing ? "animate-spin" : ""}
                  />
                  {isSyncing
                    ? t("common.loading")
                    : t("integrations.cockpit.jobSource.syncPublication.resyncButton")}
                </Button>
              ) : undefined
            }
            className="flex-1 min-h-0 space-y-3"
          />
        </Card>
      ) : (
        <div className="h-full flex flex-col min-h-0">
          <div className="flex-1 flex flex-col items-center justify-center rounded-md border border-dashed border-border p-10 text-center space-y-4 min-h-[200px]">
            <p className="text-sm text-muted-foreground">
              {t("integrations.cockpit.jobSource.publications.comparison.noVacancyInDb")}
            </p>
            {onSync && (
              <Button
                onClick={canSync ? onSync : undefined}
                disabled={!canSync || isSyncing}
                variant="default"
              >
                <Icons.SyncIcon
                  fontSize={16}
                  className={isSyncing ? "animate-spin" : ""}
                />
                {isSyncing
                  ? t("common.loading")
                  : t("integrations.cockpit.jobSource.syncPublication.button")}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
